import React, { useEffect, useMemo } from 'react';
import BackButton from '../components/BackButton';
import { useT } from '../i18n';

// ─────────────────────────────────────────────────────────────
// Terms of Service + Privacy Policy (i18n, RTL).
// "أنا البطل" operates in Israel and creates AI-personalised kids'
// stories from photos parents upload. The key, legally-sensitive
// flow — guardian consent, AI processing, and deletion of children's
// images from our databases — lives in the highlighted callouts.
//
// FILL BEFORE LAUNCH: the two placeholders below (registered legal
// entity name + jurisdiction city) are the only fields a lawyer/owner
// must complete. They render highlighted on the page so they are easy
// to spot. Retention windows (90 days / 14 days) are sensible defaults
// you can adjust.
// ─────────────────────────────────────────────────────────────
const TRADE_NAME = 'أنا البطل';
const LEGAL_NAME = '[الاسم القانوني المسجَّل للشركة]';
const JURISDICTION_CITY = '[المدينة]';
const CONTACT_EMAIL = 'anabatalbook@gmail.com';
const WHATSAPP_DISPLAY = '+972 50-502-0178';
const WHATSAPP_LINK = 'https://wa.me/972505020178';
const LAST_UPDATED = '٣٠ أيار ٢٠٢٦';

// Highlights a value the owner must fill in before publishing.
const Fill = ({ children }) => <span className="legal-fill">{children}</span>;

function Section({ title, highlight = false, children }) {
  return (
    <section className={highlight ? 'legal-section legal-section--highlight' : 'legal-section'}>
      <h2 className="legal-heading">{title}</h2>
      {children}
    </section>
  );
}

export default function Terms() {
  const t = useT();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page">
      <BackButton fallback="/" />

      <h1 className="page-title">{t.termsPageTitle}</h1>
      <p className="legal-meta">{t.termsLastUpdated}: {LAST_UPDATED}</p>

      <Section title={t.termsSection1Title}>
        <p>
          {t.termsSection1P1.split('{legalName}')[0].replaceAll('{tradeName}', TRADE_NAME)}
          <Fill>{LEGAL_NAME}</Fill>
          {t.termsSection1P1.split('{legalName}')[1]}
        </p>
        <p>{t.termsSection1P2}</p>
      </Section>

      <Section title={t.termsSection2Title}>
        <ul className="legal-list">
          {t.termsSection2Items.map((item, i) => <li key={i}>{item.replaceAll('{tradeName}', TRADE_NAME)}</li>)}
        </ul>
      </Section>

      <Section title={t.termsSection3Title}>
        <p>{t.termsSection3P1}</p>
        <p>{t.termsSection3P2}</p>
      </Section>

      <Section title={t.termsSection4Title}>
        <ul className="legal-list">
          {t.termsSection4Items.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      </Section>

      <Section title={t.termsSection5Title} highlight>
        <p>{t.termsSection5Intro}</p>
        <ul className="legal-list">
          {t.termsSection5Items.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      </Section>

      <Section title={t.termsSection6Title} highlight>
        <ul className="legal-list">
          {t.termsSection6Items.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      </Section>

      <Section title={t.termsSection7Title}>
        <ul className="legal-list">
          {t.termsSection7Items.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      </Section>

      <Section title={t.termsSection8Title}>
        <ul className="legal-list">
          {t.termsSection8Items.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      </Section>

      <Section title={t.termsSection9Title}>
        <ul className="legal-list">
          {t.termsSection9Items.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      </Section>

      <Section title={t.termsSection11Title}>
        <ul className="legal-list">
          {t.termsSection11Items.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      </Section>

      <Section title={t.termsSection12Title}>
        <p>{t.termsSection12Intro}</p>
        <ul className="legal-list">
          {t.termsSection12Items.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      </Section>

      <Section title={t.termsSection13Title}>
        <p>{t.termsSection13P1}</p>
      </Section>

      <Section title={t.termsSection14Title}>
        <p>{t.termsSection14P1}</p>
      </Section>

      <Section title={t.termsSection15Title}>
        <p>{t.termsSection15P1}</p>
      </Section>

      <Section title={t.termsSection16Title}>
        <p>{t.termsSection16P1}</p>
      </Section>

      <Section title={t.termsSection17Title}>
        <p>{t.termsSection17P1}</p>
      </Section>

      <Section title={t.termsSection18Title}>
        <p>
          {t.termsSection18P1.split('{jurisdictionCity}')[0]}
          <Fill>{JURISDICTION_CITY}</Fill>
          {t.termsSection18P1.split('{jurisdictionCity}')[1]}
        </p>
      </Section>

      <Section title={t.termsSection19Title}>
        <p>{t.termsSection19Intro}</p>
        <ul className="legal-list">
          <li>{t.termsContactEmail}: <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></li>
          <li>{t.termsContactWhatsApp}: <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer">{WHATSAPP_DISPLAY}</a></li>
        </ul>
      </Section>
    </div>
  );
}
